import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMedia } from 'react-use';
import { useNavigate } from 'react-router';
import './BasketRow.scss';
import { Button, EButtonType, formatDate, formatPrice, KitIcons, numberWithSpaces } from '@repo/apg-ui';
import classNames from 'classnames';
import parse from 'html-react-parser';
import { TIMESLOT_CART_OPTIONS } from '../../../store/products/constants';

interface Props {
  basketEntries?: any;
  product: any;
  basket?: any;
  actionsBasket: any;
  removeFromBasket: any;
  closeBasket: any;
}

export const BasketRow = ({ product, basket, basketEntries, actionsBasket, removeFromBasket, closeBasket }: Props) => {
  const navigate = useNavigate();
  const intl = useIntl();
  const isMobile = useMedia('(max-width: 768px)');

  const getProductSpotLengthNumber = () => {
    let spotLength = null;

    if (product && product.basketspotlength) {
      spotLength = product.basketspotlength;
    } else if (product && product.spotlength) {
      spotLength = product.spotlength;
    }

    return spotLength || 10;
  };

  const getProductSpotLength = () => {
    let spotLength = getProductSpotLengthNumber();

    return !!spotLength
      ? `${spotLength} ${intl.formatMessage({
          id: 'ec.general.seconds',
          defaultMessage: 'Sekunden',
        })}`
      : '';
  };
  const getSelectedTimeslotName = (timespan) => {
    const selectedTimeSlot = TIMESLOT_CART_OPTIONS.find((option) => option.timeslot === timespan);
    return selectedTimeSlot ? selectedTimeSlot.title : '-';
  };

  const getTimeslotDescription = () => {
    if (!product || !product.timeslotsinformation) {
      return '';
    }

    for (const timeslot of product.timeslotsinformation) {
      if (timeslot.timeslot === product.baskettimeslot) {
        return (
          <div className={'mb-3 d-flex flex-row'}>
            <div className='font-14 font-weight-bolder'>
              <b>
                <FormattedMessage id='ec.modals.resultList.columnTitles.timeslot' defaultMessage='Zeitfenster' />:
              </b>{' '}
              {getSelectedTimeslotName(timeslot.timeslot)}
            </div>
          </div>
        );
      }
    }
    return '';
  };

  const getPrice = () => {
    return product.pricefordates?.length > 0 ? product.pricefordates[0].price : product.standardprice;
  };

  return (
    <div className='product-row'>
      <div className='product-row-name'>{product.name}</div>

      <div className='product-row-wrapper'>
        <div className='product-row-top'>
          <div className='product-row-column-first'>
            {product.productphotopath ? (
              <div className='product-row-image'>
                <div className='code-product'>{product?.productid}</div>
                <img alt='Product' src={product.productphotopath + '&resolution=thumbnail'} />
              </div>
            ) : (
              <div className='product-row-image no-img' />
            )}
          </div>

          <div className='product-row-column-first align-items-start justify-content-center'>
            <div className='column-title'>
              <FormattedMessage id='ec.modals.resultList.columnTitles.screens' defaultMessage='Anz. Screens' />
            </div>
            <div className='column-details'>
              <div className='d-flex flex-row gap-2 align-items-center justify-content-start text-center w-100 product-icon'>
                {KitIcons.getProductIcon(product)}
                {product.mediacount && product.mediacount > 1 && <p className='mb-0'>{`${product.mediacount} `}</p>}
              </div>
            </div>
          </div>

          <div className='product-row-column-first flex-column justify-content-center'>
            <div className='column-title'>
              <FormattedMessage id='ec.modals.resultList.columnTitles.address' defaultMessage='Adresse' />
            </div>
            <div className='column-details'>
              <div className='d-flex flex-column align-content-between flex-wrap'>
                {product?.address && parse(product.address)}
                <div className='w-100'>
                  <Button
                    className='fw-bold text-decoration-underline mt-0 text-start fs-5'
                    buttonType={EButtonType.BasicLink}
                    onClick={() => {
                      navigate({
                        search: `?modalContent=product&urn=${product.producturn}`,
                      });
                    }}
                  >
                    <FormattedMessage id='ec.components.buttons.viewDetails' defaultMessage='Detail ansehen' />
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className='product-row-column-first justify-content-center termin'>
            <div className='column-title'>
              <FormattedMessage id='ec.modals.resultList.columnTitles.termin' defaultMessage='Termin' />
            </div>
            <div className='column-details'>
              <div>
                <KitIcons.GreenCheckIcon /> {product.duration}{' '}
                <FormattedMessage id='ec.modals.resultList.columnTitles.days' defaultMessage='Tage' />
              </div>
              <div>
                {formatDate(product.startdate)} -<br />
                {formatDate(product.enddate)}
              </div>
            </div>
          </div>

          <div className='product-row-column-first justify-content-center'>
            <div className='column-title'>
              <FormattedMessage id='ec.modals.resultList.columnTitles.broadcasts' defaultMessage='Ausstrahlungen' />
            </div>
            <div className='column-details'>
              <div>{getTimeslotDescription()}</div>
              <div>
                <b>
                  <FormattedMessage
                    id='ec.modals.resultList.columnTitles.totalBroadcasts'
                    defaultMessage='Total Ausstrahlungen'
                  />
                  :
                </b>{' '}
                {numberWithSpaces(product?.baskettotaltransmission)}
              </div>
              <div>
                <b>
                  <FormattedMessage id='ec.modals.resultList.columnTitles.duration' defaultMessage='Spotlänge' />:
                </b>{' '}
                {getProductSpotLength()}
              </div>
              <div>
                <b>
                  <FormattedMessage id='ec.checkout.frequency' defaultMessage='Frequency' />:
                </b>{' '}
                {product.basketfrequency} <FormattedMessage id='ec.checkout.minutes' defaultMessage='Minuten' />
              </div>
            </div>
          </div>

          <div className='product-row-column-first justify-content-center'>
            <div className='column-title'>
              <FormattedMessage id='ec.modals.resultList.columnTitles.price' defaultMessage='Preis' />
            </div>

            <div className='column-details'>
              <span className='fw-bold'>{formatPrice(getPrice())} CHF</span>
            </div>
          </div>

          <div
            className={classNames(
              'product-row-column-first product-row-column-first--checkbox product-row-actions align-items-center justify-content-center gap-3',
              basketEntries[product.producturn] ? 'flex-column' : 'flex-column-reverse',
            )}
          >
            {!isMobile && (
              <div className={'removeButton'} onClick={() => removeFromBasket(product.basketentryid)}>
                <div className={'remove-action'}>
                  <KitIcons.TrashIcon />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className='product-row-bottom-actions'>
        <div className='d-flex gap-3 pt-4'>
          {!basketEntries[product.producturn] && (
            <Button
              buttonType={basketEntries[product.producturn] ? EButtonType.InvertedPillMedium : EButtonType.PillMedium}
              className={classNames('mt-0 px-4 w-100', {
                'border-0 remove': basketEntries[product.producturn],
              })}
              id={`product-row-checkbox-${product.producturn}`}
            >
              <div className={'remove-action'}>
                <KitIcons.TrashIcon />
              </div>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
